import {isFeatureEnabled} from 'feature-flags';
import {Card, DataStatus, type Action} from 'platform/components';
import {GridItem} from 'platform/foundation';

import {useListInterestConfigurationsQuery} from '@dms/api/new-vehicle';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';
import {usePermissions} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import {ConfigurationsTable} from '../ConfigurationsTable/ConfigurationsTable';

export type InterestConfigurationsCardProps = {interestId: string};

const t = (key: string, fallback: string) => i18n.t(key, {defaultValue: fallback});

export function InterestConfigurationsCard(props: InterestConfigurationsCardProps) {
  const navigate = useNavigate();
  const [canRead, canWrite] = usePermissions({
    permissionKeys: ['newVehicleConfigurationRead', 'newVehicleConfigurationWrite'],
  });
  const isEnabled = isFeatureEnabled(featureFlags.NEW_VEHICLE_CATALOGUE);
  const query = useListInterestConfigurationsQuery(
    {interestId: props.interestId},
    {skip: !isEnabled || !canRead}
  );
  const configurations = query.currentData;

  if (!isEnabled || !canRead) {
    return null;
  }

  const configurePath = composePath(vehiclesRoutes.newVehicleConfigurator, {
    params: {step: '1'},
    queryParams: {interestId: props.interestId},
  });
  const actions: Action[] = canWrite
    ? [
        {
          type: 'button',
          variant: 'secondary',
          title: t('entity.newVehicle.actions.configure', 'Configure new vehicle'),
          onClick: () => navigate(configurePath),
        },
      ]
    : [];

  return (
    <GridItem span={2}>
      <Card
        title={t('entity.newVehicle.labels.configurations', 'New vehicle configurations')}
        actions={actions}
        data-testid="interest-new-vehicle-configurations"
      >
        <DataStatus
          isLoading={query.isLoading || query.isFetching}
          isError={query.isError}
          errorAction={{
            title: t('general.actions.tryAgain', 'Try again'),
            onClick: () => void query.refetch(),
            variant: 'secondary',
          }}
          isEmpty={configurations?.length === 0}
          emptyMessage={t(
            'entity.newVehicle.interestConfigurations.empty',
            'No new vehicle configurations yet'
          )}
          emptySubheadline={t(
            'entity.newVehicle.interestConfigurations.emptySubheadline',
            'Configure one from this interest'
          )}
        >
          <ConfigurationsTable
            configurations={configurations ?? []}
            onReview={(configurationId) =>
              navigate(
                composePath(vehiclesRoutes.newVehicleConfigurator, {
                  params: {step: '5'},
                  queryParams: {configurationId},
                })
              )
            }
          />
        </DataStatus>
      </Card>
    </GridItem>
  );
}
